import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import "./App.css";

import { Spinner } from "reactstrap";

import FormHelpers from "./helpers/FormHelpers";
import ProgressSteps from "./components/UI/Progress/ProgressSteps";
import FormArea from "./components/FormArea";
import IntroArea from "./components/IntroArea";
import PreviewArea from "./components/PreviewArea";

import config from "./store/config";
import { cardPreviewActions } from "./store/card-preview-slice";
import { fetchConfiguratorData } from "./store/configurator-actions";
import { fetchOccasionData } from "./store/card-preview-actions";

let isInitial = true;
let defaultConfiguratorState = {
  massType: "individual",
  cardType: "nocard",
  occasion: 3,
  cartItemId: 0,
  apiEndpoint: "http://localhost:25291/",
  productCollection: 1,
  csrfToken: '',
  sourceCodes: ''
};

if (typeof window.SMA_DEFAULT_CONFIG !== "undefined") {
  config.settings = window.SMA_DEFAULT_CONFIG;
  defaultConfiguratorState = window.SMA_DEFAULT_CONFIG;
}

// A ?cardtype= query param preselects the card delivery option (overrides page defaults)
const queryParams = new URLSearchParams(window.location.search);
const ALLOWED_CARD_TYPES = ["nocard", "sendtome", "sendtosomeone", "ecard"];
const cardTypeParam = (queryParams.get("cardtype") || "").toLowerCase();
if (ALLOWED_CARD_TYPES.includes(cardTypeParam)) {
  defaultConfiguratorState.cardType = cardTypeParam;
  if (config.settings) config.settings.cardType = cardTypeParam;
}

// A ?occasion= query param preselects the occasion dropdown by its label
// (e.g. ?occasion=Birthday). Matched case-insensitively against the occasion
// list after the API responds (setInitialState); unknown labels are ignored.
const occasionParam = (queryParams.get("occasion") || "").trim();
if (occasionParam) {
  defaultConfiguratorState.occasionLabel = occasionParam;
  if (config.settings) config.settings.occasionLabel = occasionParam;
}

function App() {
  const dispatch = useDispatch();

  const currentRequestLoaded = useSelector(
    (state) => state.configurator.Loaded
  );
  const currentRequest = useSelector(
    (state) => state.configurator.ConfiguratorRequest
  );

  const currentPreviewState = useSelector((state) => state.cardPreview);
  const currentOccasionLoaded = currentPreviewState.OccasionLoaded;

  const isNoCard = (currentRequest.DonorRequest.CardTypeValue === "nocard");

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // load configurator state from api
  useEffect(() => {
    if (isInitial) {
      const fetchData = async () => {
        await dispatch(fetchConfiguratorData(defaultConfiguratorState));
      };

      fetchData();
    }
  }, [dispatch, isDataLoaded]);

  // load occasion data for card preview
  useEffect(() => {
    const fetchData = async () => {
      
      const occasion = currentRequest.DonorRequest.Occasion;

      if(occasion) {
        await dispatch(fetchOccasionData(occasion));
      } else {
        dispatch(cardPreviewActions.setOccasionLoaded());
      }
      
    };

    if (currentRequestLoaded) {
      fetchData();
    }
  }, [currentRequestLoaded]);

  
  useEffect(() => {

    if(currentOccasionLoaded) {
      isInitial = false;

      // if this is an update request, set preview area details to cart item details
      if(currentRequest.DonorRequest.Id) {
        const donorRequest = currentRequest.DonorRequest;

        // set selected card
        let cardPreview = {};
        currentPreviewState.Occasion.CardPreviews.forEach((card) => {
          if(card.Id == donorRequest.CardID) {
            dispatch(cardPreviewActions.setSelectedCard(card));
          }
        });

        // set selected verse
        let verse = {};
        currentPreviewState.Occasion.Verses.forEach((verse) => {
          if(verse.Id == donorRequest.VerseID) {
            dispatch(cardPreviewActions.setSelectedVerse(verse));
          }
        });

        // set custom verse
        if(donorRequest.CustomVerseInput) {
          dispatch(cardPreviewActions.setCustomVerse(donorRequest.CustomVerseInput));
        }

        // set intention
        dispatch(
          cardPreviewActions.setFieldValue({
            key: "CardIntention",
            value: donorRequest.Intention,
          })
        );

        // set mass quantity
        dispatch(cardPreviewActions.setMassQuantity(donorRequest.MassQuantity));

        // set mass date
        dispatch(cardPreviewActions.setMassDate({
          massQuantity: donorRequest.MassQuantity,
          offeringTime: donorRequest.OfferingTime,
          massDate: donorRequest.MassDate
        }));

        // set signature
        dispatch(
          cardPreviewActions.setFieldValue({
            key: "CardSignature",
            value: donorRequest.CardSignature,
          })
        );

        const states = currentRequest.Lists.States;
        const provinces = currentRequest.Lists.Provinces;

        // set envelope recipient address
        dispatch(cardPreviewActions.setEnvelopeRecipientAddress({
          sendTo: donorRequest.SendTo,
          title: donorRequest.RecipientTitle,
          firstName: donorRequest.RecipientFirstName,
          lastName: donorRequest.RecipientLastName,
          address1: donorRequest.RecipientAddress1,
          address2: donorRequest.RecipientAddress2,
          country: donorRequest.RecipientCountry,
          state: FormHelpers.getStateProvinceCode(states, donorRequest.RecipientStateProvince),
          province: FormHelpers.getStateProvinceCode(provinces, donorRequest.RecipientStateProvince),
          city: donorRequest.RecipientCity,
          postalCode: donorRequest.RecipientZipPostalCode
        }));

        // set envelope return address
        dispatch(cardPreviewActions.setEnvelopeReturnAddress({
          useAddress: donorRequest.UseAddress,
          title: donorRequest.ReturnTitle,
          firstName: donorRequest.ReturnFirstName,
          lastName: donorRequest.ReturnLastName,
          address1: donorRequest.ReturnAddress1,
          address2: donorRequest.ReturnAddress2,
          country: donorRequest.ReturnCountry,
          state: FormHelpers.getStateProvinceCode(states, donorRequest.ReturnStateProvince),
          province: FormHelpers.getStateProvinceCode(provinces, donorRequest.ReturnStateProvince),
          city: donorRequest.ReturnCity,
          postalCode: donorRequest.ReturnZipPostalCode
        }));

      } else {
        // set mass quantity
        dispatch(cardPreviewActions.setMassQuantity(1));
      }

      setIsDataLoaded(true);
    }

  }, [currentOccasionLoaded]);

  // console.log('is no card: ' + isNoCard + ' | ' + currentRequest.DonorRequest.CardTypeValue);
  // console.log('card preview: ' + Object.keys(currentPreviewState.Occasion).length);

  if (isDataLoaded) {
    return (
      <div className="cfg">
        <ProgressSteps />
        <div className="row">
          <div className="col-sm-12 col-lg-4">
            <FormArea />
          </div>
          <div className="col-sm-12 col-lg-8">
            {(isNoCard || Object.keys(currentPreviewState.Occasion).length === 0) && <IntroArea />}
            {!isNoCard && (Object.keys(currentPreviewState.Occasion).length > 0) && <PreviewArea />}
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="loading-spinner">
        <Spinner color="primary">Loading...</Spinner>
      </div>
    );
  }
}

export default App;
